import React, { useId } from "react";

export interface SwitchProps {
    checked: boolean;
    onChange: (checked: boolean) => void;
    label?: string | React.ReactNode;
    title?: string | React.ReactNode;
    placeholder?: string | React.ReactNode;
    description?: string | React.ReactNode;
    html?: string | null;
    disabled?: boolean;
    size?: "sm" | "md" | "lg";
    className?: string;
    position?: "left" | "right";
    labelClassName?: string;
    titleClassName?: string;
    placeholderClassName?: string;
    color?: string | null;
    id?: string;
    boxed?: boolean;
}

export const Switch: React.FC<SwitchProps> = ({
    checked,
    onChange,
    label,
    title,
    placeholder,
    description,
    html = null,
    disabled = false,
    size = "md",
    className = "",
    position = "right",
    labelClassName = "",
    titleClassName = "",
    placeholderClassName = "",
    color = null,
    id,
    boxed,
}) => {
    const generatedId = useId();
    id ||= generatedId;
    const handleToggle = (): void => {
        if (!disabled) {
            onChange(!checked);
        }
    };

    const colors: Record<string, string> = {
        primary: "bg-primary",
        success: "bg-success",
        danger: "bg-danger",
        warning: "bg-amber-500",
        info: "bg-blue-600",
        neutral: "bg-neutral",
    };

    let trackColor = "bg-primary";
    if (color) {
        if (colors[color]) {
            trackColor = colors[color];
        } else {
            trackColor = color;
        }
    }

    const trackSizes =
        {
            sm: "w-9 h-5 p-0.5",
            md: "w-11 h-6 p-0.5",
            lg: "w-13 h-7 p-0.5",
        }[size] || "w-11 h-6 p-0.5";

    const thumbSizes =
        {
            sm: "w-4 h-4",
            md: "w-5 h-5",
            lg: "w-6 h-6",
        }[size] || "w-5 h-5";

    const translateClasses =
        {
            sm: checked ? "translate-x-4" : "translate-x-0",
            md: checked ? "translate-x-5" : "translate-x-0",
            lg: checked ? "translate-x-6" : "translate-x-0",
        }[size] || (checked ? "translate-x-5" : "translate-x-0");

    const hasOuterLabel = Boolean(label);
    const resolvedSubtitle = placeholder || description;
    const hasInnerContent = Boolean(title || resolvedSubtitle || html);
    const isStandalone = !hasOuterLabel && !hasInnerContent;

    const switchButton = (
        <button
            type="button"
            role="switch"
            id={id}
            aria-checked={checked}
            aria-label={
                typeof label === "string"
                    ? label
                    : typeof title === "string"
                      ? title
                      : undefined
            }
            disabled={disabled}
            onClick={(e) => {
                e.stopPropagation();
                handleToggle();
            }}
            className={`relative inline-flex flex-shrink-0 cursor-pointer rounded-full transition-colors duration-200 ease-in-out focus:outline-none ${hasInnerContent ? (position === "left" ? "mr-0" : "ml-auto") : ""} ${trackSizes} ${
                checked ? trackColor : "bg-slate-300 dark:bg-zinc-700"
            } ${disabled ? "cursor-not-allowed opacity-50" : ""}`}
        >
            <span
                className={`pointer-events-none inline-block rounded-full bg-white shadow-md transform ring-0 transition duration-200 ease-in-out ${thumbSizes} ${translateClasses}`}
            />
        </button>
    );

    // Standalone switch (without outer label or inner content) renders just the toggle button
    if (isStandalone) {
        return (
            <div className={`inline-flex items-center shrink-0 ${className}`}>
                {switchButton}
            </div>
        );
    }

    // Outer label without inner content and not explicitly boxed renders clean inline label + switch
    if (hasOuterLabel && !hasInnerContent && !boxed) {
        return (
            <label
                htmlFor={id}
                onClick={handleToggle}
                className={`flex w-full items-center gap-2.5 ${position === "right" ? "justify-between" : ""} cursor-pointer select-none text-xs font-semibold text-slate-700 dark:text-zinc-300 ${disabled ? "cursor-not-allowed opacity-60" : ""} ${className}`}
            >
                {position === "left" ? (
                    <>
                        {switchButton}
                        <span className={labelClassName}>{label}</span>
                    </>
                ) : (
                    <>
                        <span className={labelClassName}>{label}</span>
                        {switchButton}
                    </>
                )}
            </label>
        );
    }

    const isBoxed = boxed ?? hasInnerContent;

    const boxClasses = isBoxed
        ? `w-full px-3.5 py-2.5 bg-white dark:bg-dark-box border 
            border-slate-200 dark:border-white/20 rounded-xl text-xs text-slate-900 
            dark:text-white placeholder:text-slate-400 dark:placeholder:text-zinc-500 
            focus:outline-none outline-none transition-all 
            shadow-xs disabled:opacity-50 disabled:cursor-not-allowed`
        : "w-full flex items-center justify-between gap-3";

    const justifyClass = hasInnerContent
        ? "justify-between"
        : position === "left"
          ? "justify-start"
          : "justify-end";

    const innerContent = (
        <div
            onClick={handleToggle}
            data-switch-content="true"
            className={`flex items-center ${justifyClass} gap-3 ${position === "left" ? "flex-row-reverse" : ""} ${boxClasses} ${!hasOuterLabel ? className : ""} ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
        >
            {hasInnerContent && (
                <div
                    className={`flex flex-col select-none flex-1 pr-2 ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
                >
                    {title && (
                        <span
                            className={`text-xs text-slate-800 dark:text-zinc-100 leading-snug ${titleClassName}`}
                        >
                            {title}
                        </span>
                    )}
                    {resolvedSubtitle && (
                        <span
                            className={`text-xs text-slate-400 dark:text-zinc-500 leading-normal ${title ? "mt-0.5" : ""} ${placeholderClassName}`}
                        >
                            {resolvedSubtitle}
                        </span>
                    )}
                    {html && (
                        <div
                            className="text-xs text-slate-800 dark:text-zinc-100 leading-snug mt-0.5"
                            dangerouslySetInnerHTML={{ __html: html }}
                        />
                    )}
                </div>
            )}

            {switchButton}
        </div>
    );

    if (hasOuterLabel) {
        return (
            <div className={`w-full ${className}`}>
                <label
                    htmlFor={id}
                    className={`block text-xs font-bold text-slate-700 dark:text-zinc-300 mb-1 cursor-pointer select-none ${labelClassName}`}
                >
                    {label}
                </label>

                {innerContent}
            </div>
        );
    }

    return innerContent;
};
